import type { Ring, Vec2 } from '../geo/types';
import type { Channel } from '../data/types';
import { offsetPolyline, stripPolygon } from '../geo/polyline';
import type { ChannelPath } from './channelEnds';

// The strips' own miter limit: a joint corner further out than twice the wider half-width means
// the banks run nearly parallel, and those ends are left to the end extension instead.
const MITER_LIMIT = 2;
const PARALLEL = 1e-9;

// The corners that close one end of a channel strip, on its own left and right banks.
export interface JointCap {
  readonly left: Vec2;
  readonly right: Vec2;
}

export interface ChannelCaps {
  readonly head: JointCap | null;
  readonly tail: JointCap | null;
}

interface Bank {
  readonly from: Vec2;
  readonly to: Vec2;
}

interface ChannelEnd {
  readonly channel: number;
  readonly atHead: boolean;
  readonly at: Vec2;
}

interface JointEnd extends ChannelEnd {
  readonly halfWidth: number;
  readonly keepsSides: boolean;
  readonly sailLeft: Bank;
  readonly sailRight: Bank;
}

interface CapEntry extends ChannelEnd {
  readonly cap: JointCap;
}

function endBanks(channel: Channel, atHead: boolean): { left: Bank; right: Bank } {
  const half = channel.widths.map((w) => w / 2);
  const left = offsetPolyline(channel.centerline, half, MITER_LIMIT);
  const right = offsetPolyline(
    channel.centerline,
    half.map((h) => -h),
    MITER_LIMIT
  );
  const n = channel.centerline.length;
  const [end, inner] = atHead ? [0, 1] : [n - 1, n - 2];
  return {
    left: { from: left[inner]!, to: left[end]! },
    right: { from: right[inner]!, to: right[end]! },
  };
}

// The banks as a boat sees them sailing out of one channel into the other: out of a tail or into
// a head it follows the channel's own direction and keeps its left and right.
function jointEnd(channels: readonly Channel[], end: ChannelEnd, sailingIn: boolean): JointEnd {
  const { left, right } = endBanks(channels[end.channel]!, end.atHead);
  const keepsSides = end.atHead === sailingIn;
  return {
    ...end,
    halfWidth: widthAt(channels, end) / 2,
    keepsSides,
    sailLeft: keepsSides ? left : right,
    sailRight: keepsSides ? right : left,
  };
}

// Where two bank lines cross, if they cross ahead of both banks' inner ends.
function crossing(a: Bank, b: Bank): Vec2 | null {
  const u: Vec2 = [a.to[0] - a.from[0], a.to[1] - a.from[1]];
  const v: Vec2 = [b.to[0] - b.from[0], b.to[1] - b.from[1]];
  const denominator = u[0] * v[1] - u[1] * v[0];
  if (Math.abs(denominator) <= PARALLEL * Math.hypot(...u) * Math.hypot(...v)) return null;
  const w: Vec2 = [b.from[0] - a.from[0], b.from[1] - a.from[1]];
  const t = (w[0] * v[1] - w[1] * v[0]) / denominator;
  const s = (w[0] * u[1] - w[1] * u[0]) / denominator;
  return t > 0 && s > 0 ? [a.from[0] + u[0] * t, a.from[1] + u[1] * t] : null;
}

function capsAt(out: JointEnd, into: JointEnd): readonly [CapEntry, CapEntry] | null {
  const left = crossing(out.sailLeft, into.sailLeft);
  const right = crossing(out.sailRight, into.sailRight);
  const reach = MITER_LIMIT * Math.max(out.halfWidth, into.halfWidth);
  const near = (p: Vec2 | null): p is Vec2 =>
    p !== null && Math.hypot(p[0] - out.at[0], p[1] - out.at[1]) <= reach;
  if (!near(left) || !near(right)) return null;
  const entry = (end: JointEnd): CapEntry => ({
    channel: end.channel,
    atHead: end.atHead,
    at: end.at,
    cap: end.keepsSides ? { left, right } : { left: right, right: left },
  });
  return [entry(out), entry(into)];
}

function endsOf(channels: readonly Channel[]): readonly ChannelEnd[] {
  return channels.flatMap((channel, index): ChannelEnd[] => [
    { channel: index, atHead: true, at: channel.centerline[0] },
    { channel: index, atHead: false, at: channel.centerline[channel.centerline.length - 1]! },
  ]);
}

function widthAt(channels: readonly Channel[], end: ChannelEnd): number {
  const { widths } = channels[end.channel]!;
  return end.atHead ? widths[0] : widths[widths.length - 1]!;
}

// The two ends of a joint, when exactly two channels end at one point and no third channel ends
// within the wider of their widths: where more meet, their overlapping end extensions close the
// water between them.
function jointOf(channels: readonly Channel[], ends: readonly ChannelEnd[], first: ChannelEnd) {
  const within = (reach: number) =>
    ends.filter(({ at }) => Math.hypot(at[0] - first.at[0], at[1] - first.at[1]) <= reach);
  const meeting = within(0);
  const [out, into] = meeting;
  if (meeting.length !== 2 || out !== first || !into || out.channel === into.channel) return null;
  const reach = Math.max(widthAt(channels, out), widthAt(channels, into));
  return within(reach).length === 2 ? ([out, into] as const) : null;
}

// PLAN §5 step 1: where two channels meet end to end at a bend, their square ends stuck out past
// each other's banks and left notches of land and water (0.49 to 0.98 m edges where the
// Grimburgwal meets the Oudezijds Achterburgwal). Both strips end instead on the line joining the
// points where their banks cross, so they meet edge to edge like one bent channel.
export function jointCaps(channels: readonly Channel[]): readonly ChannelCaps[] {
  const ends = endsOf(channels);
  const entries = ends.flatMap((end) => {
    const joint = jointOf(channels, ends, end);
    if (!joint) return [];
    return capsAt(jointEnd(channels, joint[0], false), jointEnd(channels, joint[1], true)) ?? [];
  });
  const capAt = (channel: number, atHead: boolean) =>
    entries.find((entry) => entry.channel === channel && entry.atHead === atHead)?.cap ?? null;
  return channels.map((_, index) => ({ head: capAt(index, true), tail: capAt(index, false) }));
}

// The strip of a channel path, closed at a joined end by the joint corners instead of square.
export function cappedStrip(path: ChannelPath, caps: ChannelCaps): Ring {
  const ring = stripPolygon(path.centerline, path.widths);
  const n = path.centerline.length;
  const corners = new Map<number, Vec2>([
    ...(caps.head
      ? ([
          [0, caps.head.right],
          [2 * n - 1, caps.head.left],
        ] as const)
      : []),
    ...(caps.tail
      ? ([
          [n - 1, caps.tail.right],
          [n, caps.tail.left],
        ] as const)
      : []),
  ]);
  return ring.map((p, i) => corners.get(i) ?? p);
}
